import { useEffect, useRef, useState } from 'react';

/**
 * The backend throttles magic-link minting to one per user every `minimumSecondsBetween` seconds
 * (a conditional write inside `amazon-cognito-passwordless-auth`, default 60, with no override in
 * the CDK stack, so 60 holds in every environment and on every path that mints a link). A
 * rejected mint is not reported usefully: it surfaces as an HTTP 401 through the auth proxy or an
 * HTTP 500 through the resend endpoint, neither distinguishable from an unrelated failure, and
 * there is no 429 and no retry-after anywhere in the API. This constant matches the backend's own
 * value so useThrottledResend's countdown stops a request the backend would reject opaquely,
 * rather than reacting to a signal the backend cannot send.
 */
export const DEFAULT_THROTTLE_SECONDS = 60;

export type UseThrottledResendOptions = {
  /** Called only when the caller presses resend, and only once the countdown has reached zero.
   *  Never called while disabled. */
  onResend: () => void;
  /** Seconds the control stays disabled for, from mount and again after every press. Defaults to
   *  DEFAULT_THROTTLE_SECONDS; see that constant's doc comment for where 60 comes from. Read once,
   *  at mount, to set the first deadline: a prop change mid-countdown does not retime the
   *  in-progress countdown, only the next one a press starts. */
  throttleSeconds?: number;
  /** Disables the control while a resend request is in flight, independently of the countdown. */
  isResending?: boolean;
  /** An additional externally-owned condition that must also be false for the control to be
   *  enabled - independent of the countdown and `isResending`. Added for callers that gate the
   *  press on something of their own (e.g. a field the customer must fill in first): the control
   *  is enabled only once the countdown has expired, `isResending` is false, AND this is false.
   *  Optional and defaults to false, so every existing caller is unaffected. */
  isDisabled?: boolean;
  /** Called every time the countdown itself - not `isResending`, not `isDisabled` - crosses from
   *  running to expired: once the first time, and again after every later press whose own
   *  countdown also runs out. Never called while still counting down, and a press does not call
   *  it directly - only that press's countdown later expiring does. Lets a caller synchronise
   *  other UI to the moment a press first becomes real (e.g. revealing a field, or clearing one
   *  that could only ever have held something valid for the window that just ended) without
   *  polling this control's timer or running a second one. */
  onCountdownExpire?: () => void;
};

export type UseThrottledResendResult = {
  /** True from mount (or from the last press) until `throttleSeconds` elapses. */
  isCountingDown: boolean;
  /** Seconds left in the current countdown, ticking down to zero once per second. */
  secondsRemaining: number;
  /** True while the control must not be pressable: counting down, resending, or externally
   *  disabled. */
  disabled: boolean;
  /** Call from a press handler. A no-op while `disabled`; otherwise calls `onResend` and restarts
   *  the countdown. */
  handleResend: () => void;
};

/**
 * The state machine behind a "resend" affordance: disabled from mount until `throttleSeconds`
 * elapses, then enabled; calling `handleResend` calls `onResend` and restarts the countdown. Owns
 * its own timer and calls no API (ADR-0050) - the caller decides what a resend means via
 * `onResend`, and decides entirely how to render the countdown and the pressable control: this
 * hook carries no markup, only the state and the timing (see ThrottledResend.tsx for a thin
 * MUI-Button rendering of this same hook, and LinkSentResend.tsx / LinkSentSignInResend.tsx for
 * callers that render something else instead). A countdown on a screen is state
 * that only that screen writes (the write-lock test for view-owned state), so it lives here as a
 * hook rather than in a context or at the page.
 *
 * Per user, not per screen or tab: the backend's throttle is keyed to the user account, not to
 * this hook, so a customer with two tabs open (or two screens that each use this hook) shares one
 * backend gate that neither instance can see or coordinate with. Each instance times its own
 * local countdown independently; only the backend enforces the real limit.
 *
 * Known limitation, deliberately not handled: the countdown restarts from `throttleSeconds` on
 * every page reload, even though the backend's own window is still running, so a customer who
 * reloads mid-countdown is let through early into the opaque 401/500 the countdown exists to
 * prevent. This hook's closed option contract (onResend/throttleSeconds/isResending/isDisabled/
 * onCountdownExpire) carries no identity to key a persisted deadline by, and none of those options
 * is a safe substitute - a global storage key with no such identity would silently couple every
 * unrelated instance of this hook to the same countdown, which is worse than the gap it would
 * close. Tracked as TD-039 in docs/technical-debt.md rather than worked around.
 */
export function useThrottledResend({
  onResend,
  throttleSeconds = DEFAULT_THROTTLE_SECONDS,
  isResending = false,
  isDisabled = false,
  onCountdownExpire,
}: Readonly<UseThrottledResendOptions>): UseThrottledResendResult {
  const deadlineRef = useRef(Date.now() + throttleSeconds * 1000);
  const [secondsRemaining, setSecondsRemaining] = useState(throttleSeconds);

  useEffect(() => {
    const tick = () => {
      setSecondsRemaining(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
    };
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
    // Empty deps is correct, not a suppression: tick only reads deadlineRef.current (a ref) and
    // calls setSecondsRemaining (a setState function), and React/ESLint both treat refs and
    // setState identities as stable, so this effect has no dependency that should re-arm it.
  }, []);

  // Fires onCountdownExpire exactly on the transition into zero, not on every render that happens
  // to already be at zero (the interval above keeps ticking after expiry, but repeatedly setting
  // the same value bails out of re-rendering, so this would only ever see one "0" render per
  // cycle anyway - the ref guard is what makes that explicit rather than incidental). Read via a
  // ref, not the deps array, so a caller passing a fresh inline function every render cannot
  // cause a re-fire: only secondsRemaining actually changing should ever trigger this.
  const onCountdownExpireRef = useRef(onCountdownExpire);
  onCountdownExpireRef.current = onCountdownExpire;
  const previousSecondsRemainingRef = useRef(secondsRemaining);
  useEffect(() => {
    if (previousSecondsRemainingRef.current > 0 && secondsRemaining === 0) {
      onCountdownExpireRef.current?.();
    }
    previousSecondsRemainingRef.current = secondsRemaining;
  }, [secondsRemaining]);

  const isCountingDown = secondsRemaining > 0;
  const disabled = isCountingDown || isResending || isDisabled;

  const handleResend = () => {
    if (disabled) {
      return;
    }
    onResend();
    deadlineRef.current = Date.now() + throttleSeconds * 1000;
    setSecondsRemaining(throttleSeconds);
  };

  return { isCountingDown, secondsRemaining, disabled, handleResend };
}
